import { Link, useLocation } from 'react-router-dom';
import { useAuth } from 'zite-auth-sdk';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { LogOut, ChevronDown, Bell, BookOpen, User, Trophy, Zap, Building2, Calculator, Users, Unplug, CalendarDays, BarChart3, Shield, Settings } from 'lucide-react';
import { useDevAuth } from './DevAuthProvider';
import MobileBottomNav from './MobileBottomNav';
import { useFeatureToggles } from './FeatureToggleProvider';
import { getNotifications, markNotificationsRead, GetNotificationsOutputType } from 'zite-endpoints-sdk';
import { formatDistanceToNow, parseISO } from 'date-fns';

type Notification = GetNotificationsOutputType['notifications'][0];
const timeAgo = (d?: string) => { if (!d) return ''; try { return formatDistanceToNow(parseISO(d), { addSuffix: true }); } catch { return ''; } };

export default function Layout({ children }: { children: React.ReactNode }) {
  const { user: authUser, logout } = useAuth();
  const { isDevMode, devUser } = useDevAuth();
  const user = authUser || (isDevMode ? devUser as any : undefined);
  const location = useLocation();
  const isManager = user?.role === 'Manager' || user?.role === 'Admin' || user?.role === 'Owner';
  const isAdmin = user?.role === 'Admin' || user?.role === 'Owner';
  const canTrainingAdmin = isManager || user?.role === 'Training Admin' || user?.role === 'Course Admin';
  const canPolicyAdmin = isAdmin || user?.role === 'Training Admin' || user?.role === 'Policy Admin';
  const initials = `${user?.firstName?.[0] || ''}${user?.lastName?.[0] || ''}`;
  const name = user?.preferredName || user?.firstName || '';

  const { isRouteEnabled } = useFeatureToggles();

  const navItem = (href: string, label: string) => {
    if (!isRouteEnabled(href)) return null;
    return <DropdownMenuItem asChild><Link to={href}>{label}</Link></DropdownMenuItem>;
  };

  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);

  useEffect(() => {
    if (!user) return;
    getNotifications({}).then(d => { setNotifications(d.notifications.slice(0, 8)); setUnreadCount(d.unreadCount); }).catch(() => {});
  }, [user]);

  const handleBellOpen = async (open: boolean) => {
    setBellOpen(open);
    if (open && unreadCount > 0) {
      const ids = notifications.filter(n => !n.read).map(n => n.id);
      if (ids.length > 0) { await markNotificationsRead({ ids }).catch(() => {}); setNotifications(ns => ns.map(n => ({ ...n, read: true }))); setUnreadCount(0); }
    }
  };

  const isActive = (href: string) => href === '/' ? location.pathname === '/' : location.pathname.startsWith(href);
  const inGroup = (paths: string[]) => paths.some(p => isActive(p));
  const linkCls = (href: string) => `px-3 py-1.5 text-sm font-medium whitespace-nowrap transition-colors rounded-sm ${isActive(href) ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`;
  const groupBtnCls = (paths: string[]) => `flex items-center gap-1 px-3 py-1.5 text-sm font-medium rounded-sm transition-colors whitespace-nowrap ${inGroup(paths) ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground hover:bg-muted'}`;

  return (
    <div className={`min-h-screen bg-background flex flex-col ${isDevMode ? 'pt-6' : ''}`}>
      <header className="bg-card border-b border-border sticky top-0 z-50 shadow-sm">
        <div className="h-14 px-4 md:px-6 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0">
            <Link to="/" className="flex items-center gap-2.5 shrink-0">
              <img src="/rattlebone-logo.png" alt="Rattlebone" className="w-7 h-7 object-contain" />
              <span className="font-semibold text-foreground hidden sm:block tracking-wide uppercase text-sm">Staff Hub</span>
            </Link>

            <nav className="hidden md:flex items-center gap-0.5">
              {isRouteEnabled('/') && <Link to="/" className={linkCls('/')}>Home</Link>}
              {isRouteEnabled('/actions') && <Link to="/actions" className={linkCls('/actions')}>Actions</Link>}

              {/* Learning & Training */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={groupBtnCls(['/train', '/modules', '/compliance', '/handbook', '/sops', '/ask', '/quiz-history', '/training-builder', '/compliance-certs', '/training-renewals', '/training-videos'])}>
                    <BookOpen className="w-3.5 h-3.5 mr-1" />Learning <ChevronDown className="w-3 h-3 opacity-70" />

                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {navItem('/train', 'Interactive Training')}
                  {navItem('/modules', 'Training Modules')}
                  {navItem('/compliance', 'Compliance')}
                  {navItem('/compliance-certs', 'Certificates')}
                  <DropdownMenuSeparator />
                  {navItem('/handbook', 'Staff Handbook')}
                  {navItem('/sops', 'SOPs & Guides')}
                  {navItem('/ask', 'Ask AI')}
                  {navItem('/quiz-history', 'My Quiz History')}
                  <DropdownMenuSeparator />
                  {navItem('/training-videos', 'Training Videos')}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* My Stuff — personal HR, docs, requests */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={groupBtnCls(['/profile', '/documents', '/document-library', '/shared-documents', '/policies', '/onboarding', '/onboarding-journeys', '/my-shifts', '/leave', '/pay-leave', '/payslips', '/availability', '/uniform', '/my-keys', '/training-requests', '/reviews', '/customer-reviews', '/certificates', '/journal', '/my-rewards', '/benefits', '/recognition', '/feedback', '/suggestions', '/info-hub', '/employee-of-month'])}>
                    <User className="w-3.5 h-3.5 mr-1" />My Stuff <ChevronDown className="w-3 h-3 opacity-70" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {navItem('/profile', 'My Profile')}
                  {navItem('/documents', 'My Documents')}
                  {navItem('/shared-documents', 'Shared Documents')}
                  {navItem('/document-library', 'Document Library')}
                  {navItem('/policies', 'Policies')}
                  {navItem('/my-policies', 'My Policy Acknowledgements')}
                  <DropdownMenuSeparator />
                  {navItem('/my-shifts', 'My Shifts')}
                  {navItem('/leave', 'Leave Requests')}
                  {navItem('/pay-leave', 'Pay & Leave')}
                  {navItem('/payslips', 'My Payslips')}
                  {navItem('/availability', 'Availability')}
                  {navItem('/uniform', 'Uniform')}
                  {navItem('/my-keys', 'Keys')}
                  {navItem('/training-requests', 'Training Requests')}
                  <DropdownMenuSeparator />
                  {navItem('/reviews', 'My Reviews')}
                  {navItem('/journal', 'Journal')}
                  {navItem('/certificates', 'Certificates')}
                  <DropdownMenuSeparator />
                  {isRouteEnabled('/my-rewards') && <DropdownMenuItem asChild>
                    <Link to="/my-rewards" className="flex items-center gap-2">
                      <Trophy className="w-3.5 h-3.5 text-primary" /><span>My Rewards</span>
                      {(user?.pointsBalance ?? 0) > 0 && <span className="ml-auto flex items-center gap-0.5 text-xs text-primary font-semibold"><Zap className="w-3 h-3" />{user?.pointsBalance}</span>}
                    </Link>
                  </DropdownMenuItem>}
                  {navItem('/recognition', 'Recognition')}
                  {navItem('/benefits', 'Benefits')}
                  {navItem('/employee-of-month', 'Employee of Month')}
                  <DropdownMenuSeparator />
                  {navItem('/feedback', 'Questions & Feedback')}
                  {navItem('/suggestions', 'Ideas & Suggestions')}
                  {navItem('/info-hub', 'Info Hub')}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* The Pub + Tools */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className={groupBtnCls(['/pub-info', '/floor-plans', '/health-safety', '/tools', '/gp-calculator', '/shift-prep', '/event-setup', '/checklists', '/my-tools', '/customer-reviews'])}>
                    <Building2 className="w-3.5 h-3.5 mr-1" />Pub &amp; Tools <ChevronDown className="w-3 h-3 opacity-70" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-52">
                  {navItem('/pub-info', 'Pub Info & Q&A')}
                  {navItem('/floor-plans', 'Floor Plans')}
                  {navItem('/health-safety', 'Health & Fire Safety')}
                  {navItem('/customer-reviews', 'Customer Reviews')}
                  <DropdownMenuSeparator />
                  {navItem('/tools', 'Tools Hub')}
                  {navItem('/gp-calculator', 'GP Calculator')}
                  {navItem('/shift-prep', 'Shift Prep')}
                  {navItem('/event-setup', 'Event Setup')}
                  {navItem('/checklists', 'Checklists')}
                </DropdownMenuContent>
              </DropdownMenu>

              {/* Manager — only for Manager+ */}
              {isManager && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className={groupBtnCls(['/manager', '/team-roster', '/manager-ops', '/tasks', '/team-readiness', '/training-analytics', '/training-renewals', '/refresher-schedule', '/compliance-expiry', '/data-quality', '/engagement', '/reports', '/fire-log', '/coshh', '/maintenance', '/daily-ops-bridge', '/labour'])}>
                      <Users className="w-3.5 h-3.5 mr-1" />Manager <ChevronDown className="w-3 h-3 opacity-70" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-52">
                    {navItem('/manager', 'Team Overview')}
                    {navItem('/team-roster', 'Team Roster')}
                    {navItem('/manager-ops', 'Team Ops Centre')}
                    {navItem('/tasks', 'Tasks & Reminders')}
                    <DropdownMenuSeparator />
                    {navItem('/team-readiness', 'Team Readiness')}
                    {navItem('/training-analytics', 'Training Analytics')}
                    {navItem('/training-renewals', 'Training Renewals')}
                    {navItem('/refresher-schedule', 'Refresher Schedule')}
                    {navItem('/compliance-expiry', 'Compliance Expiry')}
                    {navItem('/data-quality', 'Data Quality')}
                    <DropdownMenuSeparator />
                    {navItem('/fire-log', 'Fire Log Book')}
                    {navItem('/coshh', 'COSHH Register')}
                    {navItem('/maintenance', 'Maintenance')}
                    <DropdownMenuSeparator />
                    {navItem('/reports', 'Reports Centre')}
                    {navItem('/engagement', 'Engagement Insights')}
                    {navItem('/daily-ops-bridge', 'Daily Ops Bridge')}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}

              {/* Admin — only for Admin+ */}
              {(isAdmin || canTrainingAdmin || canPolicyAdmin) && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button className={groupBtnCls(['/admin', '/app-settings', '/train/admin', '/training-builder', '/onboarding-journeys', '/audit-readiness', '/permission-matrix', '/email-safety', '/bridge-health', '/document-access-log', '/data-backup', '/system-docs', '/handover-docs', '/dev-board', '/feature-audit'])}>
                      <Settings className="w-3.5 h-3.5 mr-1" />Admin <ChevronDown className="w-3 h-3 opacity-70" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-56">
                    {isAdmin && navItem('/admin', 'Admin Panel')}
                    {isAdmin && navItem('/app-settings', 'App Settings')}
                    {canTrainingAdmin && navItem('/train/admin', 'Training Admin')}
                    {canPolicyAdmin && <DropdownMenuItem asChild><Link to="/train/admin?section=policies">Policy Admin</Link></DropdownMenuItem>}
                    {canTrainingAdmin && navItem('/training-builder', 'Training Builder')}
                    {isAdmin && navItem('/onboarding-journeys', 'Onboarding Journeys')}
                    <DropdownMenuSeparator />
                    {isAdmin && navItem('/audit-readiness', 'Audit & Readiness')}
                    {isAdmin && navItem('/permission-matrix', 'Permission Matrix')}
                    {isAdmin && navItem('/email-safety', 'Email Safety')}
                    {isAdmin && navItem('/document-access-log', 'Document Access Log')}
                    <DropdownMenuSeparator />
                    {isAdmin && navItem('/bridge-health', 'Bridge Health')}
                    {isAdmin && navItem('/data-backup', 'Data Backup')}
                    {isAdmin && navItem('/system-docs', 'System Docs')}
                    {isAdmin && navItem('/handover-docs', 'Handover Docs')}
                    <DropdownMenuSeparator />
                    {isAdmin && navItem('/dev-board', 'Dev Board')}
                    {isAdmin && navItem('/feature-audit', 'Feature Audit')}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </nav>
          </div>

          <div className="flex items-center gap-1 shrink-0">
            {/* Notifications */}
            <DropdownMenu open={bellOpen} onOpenChange={handleBellOpen}>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="relative w-9 h-9 p-0">
                  <Bell className="w-4 h-4" />
                  {unreadCount > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 bg-primary text-primary-foreground text-xs font-bold rounded-full flex items-center justify-center px-1 leading-none">{unreadCount > 9 ? '9+' : unreadCount}</span>}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <div className="px-3 py-2 flex items-center justify-between">
                  <p className="text-sm font-semibold text-foreground">Notifications</p>
                  <Link to="/notifications" className="text-xs text-primary hover:underline">View all</Link>
                </div>
                <DropdownMenuSeparator />
                {notifications.length === 0 ? <p className="text-xs text-muted-foreground text-center py-4">No notifications</p> : notifications.map(n => (
                  <DropdownMenuItem key={n.id} asChild>
                    <Link to={n.link || '/notifications'} className={`flex items-start gap-2.5 px-3 py-2.5 cursor-pointer ${!n.read ? 'bg-primary/5' : ''}`}>
                      <Bell className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${!n.read ? 'text-primary' : 'text-muted-foreground'}`} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs ${!n.read ? 'text-foreground font-medium' : 'text-foreground'} line-clamp-1`}>{n.title}</p>
                        {n.message && <p className="text-xs text-muted-foreground line-clamp-1 mt-0.5">{n.message}</p>}
                        <p className="text-xs text-muted-foreground mt-0.5">{timeAgo(n.createdAt)}</p>
                      </div>
                    </Link>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>

            {/* User menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm" className="gap-2 h-9 shrink-0">
                  <div className="w-7 h-7 bg-primary flex items-center justify-center text-xs font-bold text-primary-foreground rounded-sm">{initials || '?'}</div>
                  <span className="hidden sm:block text-sm max-w-28 truncate">{name}</span>
                  <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-2">
                  <p className="text-sm font-medium">{user?.firstName} {user?.lastName}</p>
                  <p className="text-xs text-muted-foreground">{user?.email}</p>
                  <p className="text-xs text-muted-foreground mt-0.5">{user?.role}{user?.department ? ` · ${user.department}` : ''}</p>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild><Link to="/profile">My Profile</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/my-tasks">My Tasks</Link></DropdownMenuItem>
                <DropdownMenuItem asChild><Link to="/notifications">Notifications</Link></DropdownMenuItem>
                <DropdownMenuSeparator />
                {isDevMode ? (
                  <DropdownMenuItem onClick={() => { sessionStorage.removeItem('__dev_token_validated'); window.location.reload(); }}><Unplug className="w-4 h-4 mr-2" /> Exit Dev Mode</DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => logout()}><LogOut className="w-4 h-4 mr-2" /> Sign out</DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      <main className="flex-1 pb-16 md:pb-0">{children}</main>
      <MobileBottomNav />
    </div>
  );
}
